/**
 * Pedal News UI: on in local dev by default; in production builds only when explicitly enabled.
 * - Development (`npm start`): visible unless `REACT_APP_ENABLE_PEDAL_NEWS=false`.
 * - Production (`npm run build`): hidden unless `REACT_APP_ENABLE_PEDAL_NEWS=true`.
 */
export function isPedalNewsUiEnabled() {
  if (process.env.REACT_APP_ENABLE_PEDAL_NEWS === 'false') {
    return false;
  }
  if (process.env.NODE_ENV === 'development') {
    return true;
  }
  return process.env.REACT_APP_ENABLE_PEDAL_NEWS === 'true';
}

/**
 * Suggested Trades strip on Match Opportunities — tabled until co-want liquidity
 * improves (~156 listing+want users today; most see 0 suggestions).
 *
 * Off by default in dev and production. To experiment locally:
 *   REACT_APP_ENABLE_SUGGESTED_TRADES=true npm start
 *
 * Code lives in src/components/recommendations/SuggestedTradesStrip.* and
 * src/utils/suggestedTrades.js; diagnose with functions/scripts/diagnose-suggested-trades.js
 */
export function isSuggestedTradesEnabled() {
  return process.env.REACT_APP_ENABLE_SUGGESTED_TRADES === 'true';
}

/**
 * "Hey, I'm interested" signals on listings.
 *
 * Shipped to all members. The create rule in firestore.rules is the real
 * security boundary; this helper exists so UI can stay consistent if we ever
 * need to gate again.
 */
export function isListingInterestEnabled() {
  return true;
}

/**
 * Giveaway promo surfaces: the dashboard hero graphic and the /giveaway page.
 *
 * Off by default everywhere since the Bad Mood giveaway closed and its winner
 * was drawn (2026-10-31). Before that, development showed the promo
 * unconditionally so it stayed reviewable while production waited for the
 * flag; that default is gone so localhost shows exactly what production will.
 * - Any build: visible only when `REACT_APP_ENABLE_GIVEAWAY_PROMO=true`
 *   (set it in `.env.production` at the next giveaway launch, the way
 *   REACT_APP_ENABLE_PEDAL_NEWS is set).
 *
 * With this off, the dashboard keeps the evergreen hero and /giveaway
 * redirects to the dashboard, so the page is unreachable rather than merely
 * unlinked. Native builds never show the promo regardless (see UserDashboard).
 */
export function isGiveawayPromoEnabled() {
  return process.env.REACT_APP_ENABLE_GIVEAWAY_PROMO === 'true';
}

/**
 * Responder badge ("Usually responds" and the two negative states) on
 * listing cards, the listing seller block and public profiles.
 * See RESPONDER_BADGE_WANTLIST_CLOSET_PLAN.md and functions/responderEvents.js.
 *
 * - Development (`npm start`): visible unless `REACT_APP_ENABLE_RESPONDER_BADGE=false`.
 * - Production (`npm run build`): hidden unless `REACT_APP_ENABLE_RESPONDER_BADGE=true`
 *   (add it to `.env.production` at launch, the way REACT_APP_ENABLE_PEDAL_NEWS is set).
 *
 * The server keeps the ledger and counters regardless of this flag, so
 * history accrues before the badge is shown.
 */
export function isResponderBadgeEnabled() {
  if (process.env.REACT_APP_ENABLE_RESPONDER_BADGE === 'false') {
    return false;
  }
  if (process.env.NODE_ENV === 'development') {
    return true;
  }
  return process.env.REACT_APP_ENABLE_RESPONDER_BADGE === 'true';
}

/**
 * "Add photos later" on the Add Pedal form (lightweight listing, plan §4).
 * Switched OFF for production on the morning of 2026-10-20 (the giveaway's
 * list-to-enter incentive plus a photo-less flow invited low-effort listings),
 * then back ON the same day with a giveaway warning in the form instead
 * (Jason): photo-less listings earn no entry until photos are added, trading
 * is unaffected. Everything downstream (placeholders, the offer photo gate,
 * snapshots) runs regardless of this flag.
 * - Development (`npm start`): visible unless `REACT_APP_ENABLE_PHOTOS_LATER=false`.
 * - Production (`npm run build`): hidden unless `REACT_APP_ENABLE_PHOTOS_LATER=true`.
 */
export function isPhotosLaterEnabled() {
  if (process.env.REACT_APP_ENABLE_PHOTOS_LATER === 'false') {
    return false;
  }
  if (process.env.NODE_ENV === 'development') {
    return true;
  }
  return process.env.REACT_APP_ENABLE_PHOTOS_LATER === 'true';
}

/**
 * "Want to send this same offer to others?" — the modal that auto-opens on
 * the offer page after a fresh trade offer and clones it to up to ten other
 * Wantlist pedals (OfferPage.jsx, shipped d6c9972 on 2026-05-30).
 *
 * Off everywhere since 2026-11-01. Five months of production data: 43 cloned
 * offers from 14 senders, zero completed trades, against 515 hand-made offers
 * and 54 completions in the same period. Recipients declined or ignored every
 * clone, and on a phone the photo-grid picker was too small to use. The owner
 * chose to keep the code and gate it rather than delete it, pending a
 * redesign. Offers already cloned keep working: the server handles
 * `isBulkSend` on existing docs regardless of this flag.
 * - Any build: visible only when `REACT_APP_ENABLE_BULK_OFFER_SEND=true`.
 */
export function isBulkOfferSendEnabled() {
  return process.env.REACT_APP_ENABLE_BULK_OFFER_SEND === 'true';
}
